import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { MapPin, ChevronDown } from "lucide-react";

/**
 * Location text input with autocomplete suggestions.
 * Fetches from /api/locations after the user types 2+ characters.
 *
 * Iter 69 — added `showAllOnFocus` (default false): when true, the input
 * pre-loads the FULL list of distinct destinations on mount and shows the
 * dropdown immediately when focused (even with no text). The user can still
 * type to filter or scroll the list to pick a city/venue. This converts the
 * input into a "dropdown of available destinations" while preserving the
 * keyboard-navigation autocomplete behaviour.
 *
 * Props:
 *  value, onChange, placeholder, kind ("pickup" | "dropoff" | "all"),
 *  testId, leadingIcon (default true), inputClassName, minChars,
 *  showAllOnFocus (default false).
 */
export default function LocationAutocomplete({
  value,
  onChange,
  placeholder = "Location",
  kind = "all",
  testId = "loc-autocomplete",
  leadingIcon = true,
  inputClassName = "rounded-xl",
  minChars = 2,
  showAllOnFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [hi, setHi] = useState(-1);
  // Cached full destination list so opening the dropdown again is instant.
  // When `showAllOnFocus`, we load the GROUPED endpoint so the list can
  // render with country headers (Albania pinned first).
  const [groups, setGroups] = useState([]);
  const [allFlat, setAllFlat] = useState([]);
  const wrapRef = useRef(null);
  const debounce = useRef(null);
  const lastQuery = useRef("");

  // Iter 69 — preload full list when in dropdown mode.
  useEffect(() => {
    if (!showAllOnFocus) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/locations/grouped", { params: { kind } });
        if (cancelled) return;
        const gs = data || [];
        setGroups(gs);
        setAllFlat(gs.flatMap((g) => g.items));
      } catch {
        if (!cancelled) { setGroups([]); setAllFlat([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [showAllOnFocus, kind]);

  // Close when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = (q) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      lastQuery.current = q;
      try {
        const params = { q, kind, limit: 8 };
        const { data } = await api.get("/locations", { params });
        if (lastQuery.current !== q) return;
        setItems(data || []);
        setHi(-1);
        setOpen(true);
      } catch {
        setItems([]);
      }
    }, 180);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (v.trim().length >= minChars) {
      fetchSuggestions(v.trim());
    } else if (showAllOnFocus) {
      // Iter 69 — empty input → show the full grouped list.
      setItems(allFlat);
      setHi(-1);
      setOpen(true);
    } else {
      setOpen(false);
      setItems([]);
    }
  };

  const handleFocus = () => {
    const v = (value || "").trim();
    if (v.length >= minChars) {
      setOpen(true);
      return;
    }
    if (showAllOnFocus) {
      setItems(allFlat);
      setHi(-1);
      setOpen(true);
    }
  };

  const choose = (val) => {
    onChange(val);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => (h + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      if (hi >= 0) {
        e.preventDefault();
        choose(items[hi]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="flex items-center gap-2">
        {leadingIcon && <MapPin className="h-4 w-4 text-neutral-500 shrink-0" />}
        <Input
          value={value || ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={onKey}
          placeholder={placeholder}
          className={inputClassName}
          autoComplete="off"
          data-testid={testId}
        />
        {showAllOnFocus && (
          // Iter 69 — explicit chevron makes it visually obvious this is a
          // dropdown. Tapping it toggles the list (same behaviour as focusing
          // the input, but more discoverable on touch).
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); setItems(allFlat); setHi(-1); setOpen((o) => !o); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition"
            aria-label="Toggle destinations list"
            data-testid={`${testId}-chevron`}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && items.length > 0 && (() => {
        // Iter 69 — when an explicit `q` filter is in play, show a flat
        // result list (single typing path). Otherwise show the GROUPED
        // dropdown with country headers + flags, Albania pinned first.
        const isFiltered = (value || "").trim().length >= minChars && !showAllOnFocus
          ? false
          : (value || "").trim().length >= minChars;
        const usingGroups = showAllOnFocus && !isFiltered && groups.length > 0;

        if (!usingGroups) {
          return (
            <ul
              className="absolute z-50 left-0 right-0 mt-2 max-h-72 overflow-auto rounded-xl border border-border bg-white shadow-lg py-1"
              data-testid={`${testId}-list`}
            >
              {items.map((it, idx) => (
                <li key={it}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); choose(it); }}
                    onMouseEnter={() => setHi(idx)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${idx === hi ? "bg-blue-50 text-blue-700" : "hover:bg-neutral-50"}`}
                    data-testid={`${testId}-option-${idx}`}
                  >
                    <MapPin className="h-3.5 w-3.5 text-neutral-400" /> {it}
                  </button>
                </li>
              ))}
            </ul>
          );
        }

        // Grouped layout — render each country block with a sticky header.
        // Track a running index so keyboard navigation still maps onto the
        // flat `items` array (`hi` matches the i-th rendered button).
        let runningIdx = -1;
        return (
          <div
            className="absolute z-50 left-0 right-0 mt-2 max-h-80 overflow-auto rounded-xl border border-border bg-white shadow-lg py-1"
            data-testid={`${testId}-list`}
          >
            {groups.map((grp) => (
              <div key={grp.country} data-testid={`${testId}-group-${grp.country.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-3 py-1.5 border-b border-neutral-100 flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-neutral-600">
                  {grp.flag && <span className="text-base leading-none">{grp.flag}</span>}
                  <span>{grp.country}</span>
                  <span className="ml-auto text-neutral-400 normal-case tracking-normal text-[11px] font-medium">{grp.items.length}</span>
                </div>
                {grp.items.map((it) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  return (
                    <button
                      key={`${grp.country}-${it}`}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); choose(it); }}
                      onMouseEnter={() => setHi(idx)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${idx === hi ? "bg-blue-50 text-blue-700" : "hover:bg-neutral-50"}`}
                      data-testid={`${testId}-option-${idx}`}
                    >
                      <MapPin className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                      <span className="truncate">{it}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })()}
      {open && items.length === 0 && showAllOnFocus && (
        <div
          className="absolute z-50 left-0 right-0 mt-2 rounded-xl border border-border bg-white shadow-lg py-3 px-3 text-xs text-neutral-500"
          data-testid={`${testId}-empty`}
        >
          No destinations available yet.
        </div>
      )}
    </div>
  );
}
